import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from 'sonner';

interface ServiceNumberState {
  serviceNumber: string | null;
  isLoading: boolean;
  error: Error | null;
}

export function useServiceNumber(taxReturnId: string | undefined) {
  const [state, setState] = useState<ServiceNumberState>({
    serviceNumber: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    if (!taxReturnId) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    let channel: RealtimeChannel;

    const fetchServiceNumber = async () => {
      try {
        // First, get the tax return request to check if it has a service_id
        const { data: taxReturn, error: taxReturnError } = await supabase
          .from('tax_return_requests')
          .select('service_id')
          .eq('id', taxReturnId)
          .single();

        if (taxReturnError) throw taxReturnError;

        if (taxReturn?.service_id) {
          // If there's a service_id, fetch the service number
          const { data: service, error: serviceError } = await supabase
            .from('services')
            .select('service_number')
            .eq('id', taxReturn.service_id)
            .single();

          if (serviceError) throw serviceError;

          setState({
            serviceNumber: service?.service_number || null,
            isLoading: false,
            error: null,
          });
        } else {
          setState({
            serviceNumber: null,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        console.error('Error fetching service number:', error);
        setState({
          serviceNumber: null,
          isLoading: false,
          error: error as Error,
        });
      }
    };

    // Initial fetch
    fetchServiceNumber();

    // Set up real-time subscription for tax return updates
    channel = supabase.channel('tax-return-service-updates')
      .on(
        'postgres_changes' as 'system',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tax_return_requests',
          filter: `id=eq.${taxReturnId}`,
        },
        async (payload) => {
          console.log('Tax return updated:', payload);
          await fetchServiceNumber();
        }
      )
      .on(
        'postgres_changes' as 'system',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'services',
          filter: `id=eq.${taxReturnId}`,
        },
        async (payload) => {
          console.log('Service updated:', payload);
          await fetchServiceNumber();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('Subscribed to service number updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Error subscribing to service number updates');
          toast.error('Error subscribing to service number updates');
        }
      });

    return () => {
      if (channel) {
        console.log('Unsubscribing from service number updates');
        supabase.removeChannel(channel);
      }
    };
  }, [taxReturnId]);

  return state;
}